<template>
  <div v-if="hasPrices">
    <QuoteSummary
      :cover="getCoverLandlords"
      @click="customiseQuote"
      @showReplacementProductDialog="showReplacementProductDialog"
      handleBackBtnExternally
      @click:backButton="$router.push({ name: 'PropertyDetailsPage' })"
      heading="Your Landlord's Insurance quotes"
      @showDrawerChangeCover="
        isShowDrawerChangeCover = !isShowDrawerChangeCover
      "
    ></QuoteSummary>

    <div class="d-flex pl-4 mt-6 align-center pr-4 flex-wrap">
      <div
        class="d-flex flex-column flex-lg-row flex-wrap gc-5 gr-2"
        v-if="isShowPremiumFlex"
      >
        <div class="d-flex align-center gc-2">
          <h3 class="d-none d-md-block font-20">Premium flex</h3>
          <h3 class="d-md-none font-20">Flex</h3>
          <BaseTooltip
            :text="TOOL_TIP_TEXT.PremiumFlex"
            icon="mdi-help-circle"
          />
        </div>

        <FlexSpinner
          @input="calculateFlex"
          class="font-18"
          type="percentage"
          :maxCommissionSacrifice="getMaxCommissionSacrifice"
          :initialVal="commissionSacrifice"
        />
      </div>
      <div
        class="d-flex flex-column align-sm-end flex-md-row ml-auto order-md-3"
      >
        <BaseButton
          text="Share all"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3 d-flex align-center mr-xl-9"
          icon="mdi-share-variant"
          color="#106175"
          small
          @click="shareResults()"
        />
        <BaseButton
          text="Print all"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3 d-flex align-center mt-1 mt-md-0"
          icon="mdi-printer"
          color="#106175"
          small
          @click="printAllQuotes()"
        />
      </div>
    </div>

    <TheQuotes
      @shareQuote="shareQuote"
      @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
      @showPremiumBreakdownMonthly="showPremiumBreakdownMonthly"
      @showEndorsementDetails="showEndorsementDetails"
      @printQuotes="printQuote"
      @setAd="setAd"
      @showPayMonthly="showPayMonthly"
      @click:applyBtn="applyForQuote"
      @showWhatsIncluded="showWhatsIncluded"
      hideADs
      handleApplyBtnExternally
      :productGroup="productGroup"
    />

    <DrawerCustomiseCover
      v-model="isShowDrawerCustomiseCover"
      :cover="getCoverLandlords"
      :additionalCover="ADD_ONS_LANDLORDS"
      :additionalCoverAnnualCost="ADD_ONS_ANNUAL_COST_LANDLORDS"
      @refreshCover="refreshCover()"
      :productGroup="productGroup"
      @editCover="isShowDrawerChangeCover = !isShowDrawerChangeCover"
    ></DrawerCustomiseCover>

    <DrawerChangeCover
      :coverTypeItems="
        $vuetify.breakpoint.lgAndUp
          ? COVER_TYPE_ITEMS
          : COVER_TYPE_ITEMS_LANDLORDS_SMALL
      "
      v-model="isShowDrawerChangeCover"
    />

    <BadgeDefaqto :show="isShowBadgeDefaqto" />

    <BaseDialog
      ref="shareDialog"
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="data?.content"
          :quoteId="data?.quoteId"
          :quoteRequestId="data?.quoteRequestId"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdownModel="data"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="endorsementDetails"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pt-6 pb-3"
      headerDivider
    >
      <template v-slot:content="{ data }">
        <template v-if="data?.Description">
          <div v-html="data?.Description" class="pa-10"></div>
        </template>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="printQuotes"
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-3"
      contentClass="mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <BaseDialog
      ref="payMonthly"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
    >
      <template v-slot:content>
        <DialogContentPayMonthly />
      </template>
    </BaseDialog>
    <BaseDialog
      ref="whatsIncluded"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
      persistent
      :max-width="720"
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedAccidentalDamage
          @closeWhatsIncludedDialog="closeWhatsIncludedDialog"
        />
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { TOOL_TIP_TEXT, EXCLUDED } from "@/constants/common.constants.js";
import { ROUTE_NAME } from "@/constants/route.constants";
import documentService from "@/services/document.service";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { filterSingleQuoteDocs, filterMultiQuoteDocs } from "@/common/docs";
import { adBoolToString, adStringToBool } from "@/common/cover";
import { getProductGroup } from "@/common/question-set";
import {
  ADD_ONS_LANDLORDS,
  ADD_ONS_ANNUAL_COST_LANDLORDS,
} from "@/constants/additional-cover-options";
import {
  COVER_TYPE_ITEMS,
  COVER_TYPE_ITEMS_LANDLORDS_SMALL,
} from "@/constants/component.constants.js";

export default {
  name: "YourQuotesPage",
  components: {
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    FlexSpinner: () => import("@/components/QuoteResult/FlexSpinner.vue"),
    QuoteSummary: () => import("@/components/QuoteResult/QuoteSummary.vue"),
    BaseTooltip: () => import("@/components/BaseComponents/BaseTooltip.vue"),
    TheQuotes: () => import("@/components/QuoteResult/TheQuotes.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentShareQuotes: () =>
      import("@/components/QuoteResult/DialogContentShareQuotes.vue"),
    DialogContentPrintQuotes: () =>
      import("@/components/QuoteResult/DialogContentPrintQuotes.vue"),
    DialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/DialogContentPremiumBreakdown.vue"),
    DialogContentPayMonthly: () =>
      import("@/components/QuoteResult/DialogContentPayMonthly"),
    DrawerCustomiseCover: () =>
      import("@/components/QuoteResult/DrawerCustomiseCover.vue"),
    DrawerChangeCover: () =>
      import("@/components/QuoteResult/DrawerChangeCover.vue"),
    BadgeDefaqto: () => import("@/components/QuoteResult/BadgeDefaqto.vue"),
    DialogContentWhatsIncludedAccidentalDamage: () =>
      import(
        "@/components/QuoteResult/DialogContentWhatsIncludedAccidentalDamage"
      ),
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      isShowDrawerCustomiseCover: false,
      TOOL_TIP_TEXT,
      ADD_ONS_ANNUAL_COST_LANDLORDS,
      ADD_ONS_LANDLORDS,
      documentList: [],
      selectedAds: {
        buildingsAd: EXCLUDED,
        contentsAd: EXCLUDED,
      },
      isShowDrawerChangeCover: false,
      COVER_TYPE_ITEMS,
      COVER_TYPE_ITEMS_LANDLORDS_SMALL,
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getCoverLandlords",
      "hasPrices",
      "getQuoteRequestId",
      "httpConfig",
      "insurerQuotesModel",
      "coverSelection",
      "isShowPremiumFlex",
      "callChannel",
      "getMaxCommissionSacrifice",
      "replacementProductAvailable",
      "qsId",
    ]),
    ...mapState(QUESTIONS_MODULE, ["commissionSacrifice", "overlay"]),
    productGroup() {
      return getProductGroup(this.qsId);
    },
    isShowBadgeDefaqto() {
      if (this.isShowDrawerCustomiseCover) return false;
      if (this.isShowDrawerChangeCover) return false;
      return true;
    },
  },
  mounted() {
    // Restore accidental damage selections
    this.selectedAds.buildingsAd = adBoolToString(
      this.coverSelection.buildingsAd
    );
    this.selectedAds.contentsAd = adBoolToString(
      this.coverSelection.contentsAd
    );
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "updateCommissionSacrifice",
      "setCoverSelection",
      "setOverlayVisible",
      "saveQuoteResults",
      "setRestoringLastStep",
    ]),
    applyForQuote() {
      this.$router.push({ name: ROUTE_NAME.LANDLORDS.B2B.APPLICATION });
    },
    calculateFlex(val) {
      this.updateCommissionSacrifice(val);
    },
    async shareResults() {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", {
        content: { ...this.getCoverLandlords, ...this.selectedAds },
        quoteRequestId: this.getQuoteRequestId,
      });
    },
    async shareQuote(quoteId) {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", {
        content: this.getCoverLandlords,
        quoteId: quoteId,
      });
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    setAd(data) {
      this.selectedAds.buildingsAd = adBoolToString(data.buildingsAd);
      this.selectedAds.contentsAd = adBoolToString(data.contentsAd);
      this.setCoverSelection({
        buildingsAd: data.buildingsAd,
        contentsAd: data.contentsAd,
      });
    },
    printQuote(quote) {
      this.setOverlayVisible(true);
      this.getDocumentList()
        .then(() => {
          const data = {
            ...quote,
            files: this.documentList.filter((doc) =>
              filterSingleQuoteDocs(doc, quote.QuoteId, "annual")
            ),
            type: "single",
          };
          this.setOverlayVisible(false);
          this.$refs.printQuotes.open("Download for printing", data);
        })
        .catch((error) => {
          this.setOverlayVisible(false);
          console.error(error);
        });
    },
    printAllQuotes() {
      this.setOverlayVisible(true);
      this.getDocumentList()
        .then(() => {
          const data = {
            type: "multi",
            selectedAds: this.selectedAds,
            files: this.documentList
              .filter((doc) => filterMultiQuoteDocs(doc, this.selectedAds))
              .reverse(),
            quoteRequestId: this.getQuoteRequestId,
          };
          this.setOverlayVisible(false);
          this.$refs.printQuotes.open("Download for printing", data);
        })
        .catch((error) => {
          this.setOverlayVisible(false);
          console.error(error);
        });
    },
    async getDocumentList() {
      await this.saveQuoteResults().catch((e) => {
        throw e;
      });
      if (this.documentList.length) {
        return this.documentList;
      }
      return documentService
        .getDocumentList(
          this.getQuoteRequestId,
          this.callChannel,
          this.httpConfig
        )
        .then((res) => {
          if (res.StatusCode === "OK") {
            res.Documents.forEach((doc) => {
              doc.isDownloading = false;
              doc.isDownloaded = false;
            });

            //The code below is to have a copy of these files for each quote so we could track their download state.
            //only two records of ipid and terms are coming back from api, we need one for each quote for their download state.
            const termFile = res.Documents.find((doc) => doc.Code === "terms");
            const ipidFile = res.Documents.find((doc) => doc.Code === "ipid");
            const factfindsdn = res.Documents.find(
              (doc) => doc.Code === "factfindsdn"
            );
            this.insurerQuotesModel.forEach((quote) => {
              if (quote.isAvailable) {
                quote.quotes.forEach((insurerQuote) => {
                  res.Documents.push({
                    ...termFile,
                    QuoteId: insurerQuote.QuoteId,
                  });
                  res.Documents.push({
                    ...ipidFile,
                    QuoteId: insurerQuote.QuoteId,
                  });
                  res.Documents.push({
                    ...factfindsdn,
                    QuoteId: insurerQuote.QuoteId,
                  });
                });
              }
            });
            this.documentList = res.Documents;
            return this.documentList;
          }
        })
        .catch((error) => {
          throw error;
        });
    },
    customiseQuote() {
      if (this.replacementProductAvailable) {
        this.showReplacementProductDialog();
      } else {
        this.isShowDrawerCustomiseCover = true;
      }
    },
    showReplacementProductDialog() {
      this.$refs.productTransfer.open();
    },
    showEndorsementDetails(endorsement) {
      this.$refs.endorsementDetails.open(endorsement.Title, endorsement);
    },
    showPayMonthly() {
      this.$refs.payMonthly.open("More details");
    },
    refreshCover() {
      this.documentList = [];
      const buildingsAd = adStringToBool(this.selectedAds.buildingsAd);
      const contentsAd = adStringToBool(this.selectedAds.contentsAd);
      // Update global state
      this.setCoverSelection({ buildingsAd, contentsAd });
      // Update local models
      this.insurerQuotesModel.forEach((insurer) => {
        insurer.contentsAd = contentsAd;
        insurer.buildingsAd = buildingsAd;
        this.setInsurerQuote(insurer);
      });
    },
    setInsurerQuote(insurer) {
      // Update the local insurer model if it exists (updates screen)
      if (insurer.isAvailable && insurer.quotes) {
        insurer.selectedQuote = insurer.quotes.find(
          (q) =>
            q.BuildingsAD === insurer.buildingsAd &&
            q.ContentsAD === insurer.contentsAd
        );
      }
    },
    showWhatsIncluded() {
      this.$refs.whatsIncluded.open("What's included?");
    },
    closeWhatsIncludedDialog() {
      this.$refs.whatsIncluded.cancel();
    },
  },
};
</script>

<style lang="scss" scoped>
@media screen and (max-width: ($grid-breakpoints-sm - 1)) {
  .additional-content {
    border-top: 1px solid #ccd6dd;
  }
}

.cut-off-message {
  border: 2px solid #fb8c00 !important;
  padding: 10px;
}
</style>
